/**
 * PipelineBar — five-segment status bar showing live trial counts across the
 * pipeline, sized proportionally by count. Operator vocabulary maps the 5 board
 * lanes to friendly labels (Waiting / Claimed / Running / Recovering / Done).
 *
 * Motion budget: ONE restrained pulse on the Running segment when it has trials.
 * Waiting carries no motion. Done is visually recessed. Purely presentational —
 * no fetch, no polling, no global reads inside the component body.
 */

import { LANES } from './board-lanes'
import type { LaneId } from './board-lanes'

export interface PipelineBarProps {
  /** Per-lane trial counts derived from the host-lens lane rollup. */
  laneCounts: Record<LaneId, number>
  /** Trailing 5-minute completions per minute (0 before the first tick). */
  throughputPerMin: number
}

/**
 * Operator vocabulary for each lane. Keyed by the canonical LaneId from
 * board-lanes.ts so that adding a 6th lane to that file surfaces here as a
 * type error rather than a silent omission.
 */
const OPERATOR_LABELS: Readonly<Record<LaneId, string>> = {
  queued: 'Waiting',
  leased: 'Claimed',
  running: 'Running',
  recovering: 'Recovering',
  done: 'Done',
}

export function PipelineBar({ laneCounts, throughputPerMin }: PipelineBarProps): React.JSX.Element {
  const total = LANES.reduce((s, { id }) => s + laneCounts[id], 0)

  return (
    <div className="pipeline-bar">
      <div className="pb-track" aria-label="Pipeline status bar">
        {LANES.map(({ id }) => {
          const label = OPERATOR_LABELS[id]
          const count = laneCounts[id]
          // Equal-width segments when total is 0 (empty-state hint)
          const pct = total > 0 ? (count / total) * 100 : 100 / LANES.length
          const isRunning = id === 'running'
          const hasWork = count > 0
          return (
            <div
              key={id}
              className={`pb-seg pb-seg-${id}${isRunning && hasWork ? ' pb-seg-running-active' : ''}`}
              style={{ flexBasis: `${pct}%` }}
              title={`${label}: ${count}`}
              aria-label={`${label}: ${count}`}
            >
              {/* ONE restrained motion indicator on Running when it has work */}
              {isRunning && hasWork && <span className="pb-running-tick" aria-hidden="true" />}
            </div>
          )
        })}
      </div>

      <div className="pb-footer">
        <div className="pb-legend">
          {LANES.map(({ id }) => (
            <div key={id} className={`pb-legend-item pb-legend-${id}`}>
              <span className="pb-legend-count">{laneCounts[id]}</span>
              <span className="pb-legend-label">{OPERATOR_LABELS[id]}</span>
            </div>
          ))}
        </div>
        <div
          className="pb-throughput"
          aria-label={`Throughput: ${throughputPerMin} trials per minute (5-minute window)`}
        >
          <span className="pb-throughput-value">{throughputPerMin}</span>
          <span className="pb-throughput-unit">/min</span>
          <span className="pb-throughput-window">(5m)</span>
        </div>
      </div>
    </div>
  )
}
